/**
 * Estado da cobranca da licenca do sistema (ver api/app/lib/ConfigPgLib.js).
 *
 * O estado vive num `Vue.observable` exportado daqui, e nao no Vuex, por dois
 * motivos: e um dado de infraestrutura que nenhuma tela do sistema consome
 * (so o AvisoPagamento), e manter fora do store evita editar store/index.js —
 * arquivo que diverge entre as branches de cliente.
 *
 * Fail-open em todo caminho de erro: rede fora, 401, resposta estranha — nada
 * disso pode virar um aviso de cobranca na cara do operador.
 */

import Vue from "vue";
import axios from "@/utils/axios";

// O aviso e consultado a cada navegacao. Sem TTL, clicar rapido entre telas
// dispararia um GET por clique.
const TTL_MS = 60 * 1000;

const VAZIO = {
  estado: "OK",
  titulo: null,
  mensagem: null,
  pix_copia_cola: null,
  competencia: null,
  vencimento: null,
  dias_atraso: 0,
  qtd_parcelas: 0,
  valor_total: 0,
  bloqueia_em: null,
};

export const estadoPagamento = Vue.observable(
  Object.assign({ carregado: false }, VAZIO)
);

let expiraEm = 0;
let emVoo = null;

/**
 * Só mexe em chaves que ja existem no observable — chave nova em Vue 2 nao e
 * reativa e o banner nunca atualizaria.
 */
const aplicar = (dados) => {
  const novo = Object.assign({}, VAZIO, dados || {});
  Object.keys(VAZIO).forEach((k) => {
    estadoPagamento[k] = novo[k];
  });
  estadoPagamento.carregado = true;
};

export const estaVencido = () =>
  estadoPagamento.estado === "VENCIDO" || estadoPagamento.estado === "BLOQUEADO";

export const temAviso = () => estadoPagamento.estado !== "OK";

/**
 * Busca o estado. Respeita o TTL, a nao ser que `forcar` seja true.
 * Nunca rejeita.
 */
export const atualizarPagamento = (forcar) => {
  if (!forcar && Date.now() < expiraEm) return Promise.resolve(estadoPagamento);
  if (emVoo) return emVoo;

  emVoo = axios
    .get("/pg/status")
    .then((res) => {
      const dados = res && res.data && res.data.data;
      aplicar(dados && dados.estado ? dados : null);
    })
    .catch(() => {
      // Sem token, API fora, rota inexistente (instalacao onde a migration
      // ainda nao rodou): segue como estava, sem avisar nada.
    })
    .then(() => {
      expiraEm = Date.now() + TTL_MS;
      emVoo = null;
      return estadoPagamento;
    });

  return emVoo;
};

/**
 * Chamado pelo interceptor quando a API recusa uma gravacao com 403
 * PAGAMENTO_BLOQUEADO. Promove o estado local na hora — o TTL poderia deixar o
 * operador ate um minuto sem entender por que o "salvar" parou de funcionar.
 */
export const marcarBloqueado = (erro) => {
  estadoPagamento.estado = "BLOQUEADO";
  if (erro && erro.message) estadoPagamento.mensagem = erro.message;
  if (erro && erro.titulo) estadoPagamento.titulo = erro.titulo;
  if (erro && erro.vencimento) estadoPagamento.vencimento = erro.vencimento;
  if (erro && typeof erro.dias_atraso === "number")
    estadoPagamento.dias_atraso = erro.dias_atraso;
  estadoPagamento.carregado = true;
  expiraEm = 0; // a proxima navegacao busca os detalhes (pix, valor, competencia)
};

export const limparPagamento = () => {
  aplicar(null);
  estadoPagamento.carregado = false;
  expiraEm = 0;
};
